import React from 'react';
import {
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  type MenuProps as AriaMenuProps,
  MenuTrigger as RACMenuTrigger,
  type MenuTriggerProps,
  SubmenuTrigger as RACSubmenuTrigger,
  type SubmenuTriggerProps,
  type MenuItemProps,
  Separator,
  type SeparatorProps,
  composeRenderProps,
} from 'react-aria-components';

import { DropdownSection, type DropdownSectionProps, dropdownItemStyles } from './list-box';
import { Popover, type PopoverProps } from './popover';
import { ChevronLeftSVG } from 'components/iconsSVG/chevronLeft';
import { CheckSVG } from 'components/iconsSVG/check';
import { isDomain } from 'utils/domain';

export function MenuTrigger(props: MenuTriggerProps) {
  return <RACMenuTrigger {...props} />;
}

export function SubmenuTrigger(props: SubmenuTriggerProps) {
  return <RACSubmenuTrigger {...props} />;
}

interface MenuProps<T> extends AriaMenuProps<T> {
  placement?: PopoverProps['placement'];
  border?: PopoverProps['border'];
}

export function Menu<T extends object>({ placement = 'bottom', border = true, ...props }: MenuProps<T>) {
  return (
    <Popover placement={placement} className="min-w-[var(--trigger-width)]" border={border}>
      <AriaMenu {...props} className="h-full max-h-[inherit] overflow-auto outline outline-0" />
    </Popover>
  );
}

export function MenuItem(props: MenuItemProps) {
  return (
    <AriaMenuItem {...props} className={composeRenderProps(props.className, (className, renderProps) => dropdownItemStyles({ className, ...renderProps }))}>
      {composeRenderProps(props.children, (children, { selectionMode, isSelected, isFocused, hasSubmenu }) => (
        <>
          {selectionMode !== 'none' && <span className="flex w-4 items-center">{isSelected && <CheckSVG aria-hidden className="h-4 w-4" />}</span>}
          <span className="flex flex-1 items-center gap-2 truncate font-normal group-selected:font-semibold">{children}</span>
          {hasSubmenu && <ChevronLeftSVG aria-hidden className="absolute right-2 h-4 w-4 rotate-180" />}
        </>
      ))}
    </AriaMenuItem>
  );
}

export function MenuSeparator(props: SeparatorProps) {
  return <Separator {...props} className="mx-3 my-1 border-b border-subtle" />;
}

export function MenuSection<T extends object>(props: DropdownSectionProps<T>) {
  return <DropdownSection {...props} />;
}
